import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { Wallet, CheckCircle2, XCircle, Clock, ShieldCheck, FileText, History } from "lucide-react";

const SEED_CURRENCIES = ["INR", "USD", "GBP", "AED", "EUR"];

// Production Partner Workspace PP2a (2026-02): NJM-side Rate Card review queue —
// founder/partner/pm/finance only (`partner_rate_card.admin` scope). A PM can propose changes
// on a partner's behalf here (source="pm"); founder/finance/partner(-lead) can approve — a
// SINGLE signature from any one of them promotes a pending version immediately (2026-02
// governance simplification, was a two-party Founder+Finance sign-off) — enforced server-side,
// this page just hides the button for clarity. Reached only via the "Manage Rate Cards" link
// on /admin, off the main landing.
//
// PP2c (2026-02) onboarding refinement: `/initialize` (blank, structure-only — no rate, no
// internal_cost) is now the DEFAULT action for any new partner, available to the whole admin
// scope (founder/pm/finance/internal partner) since a PM routinely does this on a partner's
// behalf. `/seed-from-global` is demoted to a secondary, founder-only, clearly-labeled
// historical bootstrap (TVC's own case) — copying NJM's own internal_cost as a starting price
// is the WRONG default for any future partner, correct only as a one-off migration.

function GroupTag({ group }) {
  const style = { Crew: "bg-[#3A3A38] text-white", Equipment: "bg-[#8A6A0A] text-white", Services: "bg-[#5E7A8A] text-white" }[group] || "bg-[#F5F5F4] text-[#57534E]";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium ${style}`}>{group}</span>;
}

function PartnerListRow({ row, selected, onSelect }) {
  return (
    <button onClick={() => onSelect(row.partner_remote_id)} data-testid={`admin-rate-card-partner-row-${row.partner_remote_id}`}
      className={`w-full text-left px-4 py-3 border-b border-[#E7E5E4] flex items-center justify-between gap-3 ${selected ? "bg-[#FFC817]/10" : "hover:bg-[#FAFAF9]"}`}>
      <div className="min-w-0">
        <div className="text-sm text-[#1C1C1E] font-medium truncate">{row.partner_name}</div>
        <div className="text-[10px] text-[#A8A29E] uppercase tracking-wider mt-0.5">{row.currency}</div>
      </div>
      {row.has_card ? (
        row.pending_count > 0 ? (
          <span className="rounded-full bg-amber-500/10 text-amber-600 px-2 py-0.5 text-[10px] font-medium shrink-0 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {row.pending_count} pending</span>
        ) : (
          <span className="rounded-full bg-emerald-500/10 text-emerald-600 px-2 py-0.5 text-[10px] font-medium shrink-0">{row.status}</span>
        )
      ) : (
        <span className="rounded-full bg-[#F5F5F4] text-[#A8A29E] px-2 py-0.5 text-[10px] font-medium shrink-0">No card yet</span>
      )}
    </button>
  );
}

export default function AdminPartnerRateCards() {
  const { hasScope, user } = useAuth();
  const canManage = hasScope("partner_rate_card.admin");
  const canSign = user?.role === "founder" || user?.role === "finance" || user?.role === "partner";
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyVersion, setBusyVersion] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [seedCurrency, setSeedCurrency] = useState("INR");
  const [seeding, setSeeding] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [showBootstrap, setShowBootstrap] = useState(false);

  const loadList = useCallback(() => {
    if (!canManage) { setLoading(false); return; }
    api.get("/v1/partner-portal/admin/rate-cards").then((r) => {
      setRows(r.data.items || []);
      if (!selected && r.data.items?.length) setSelected(r.data.items[0].partner_remote_id);
    }).finally(() => setLoading(false));
  }, [canManage, selected]);

  useEffect(loadList, [loadList]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.get(`/v1/partner-portal/admin/rate-cards/${selected}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [selected]);

  const currentRow = rows.find((r) => r.partner_remote_id === selected);
  const pendingVersionId = currentRow?.pending_version_id;

  // Production Partners module Phase A (2026-02): pre-fill the currency picker from the
  // partner's own Default Currency field whenever a no-card partner is selected, instead of
  // always defaulting to INR — only overrides the picker, never an existing card's currency.
  useEffect(() => {
    if (currentRow && !currentRow.has_card && currentRow.default_currency) setSeedCurrency(currentRow.default_currency);
  }, [currentRow]);

  const act = async (action) => {
    if (!pendingVersionId) return;
    setBusyVersion(pendingVersionId);
    try {
      if (action === "approve") {
        await api.post(`/v1/partner-portal/admin/rate-cards/versions/${pendingVersionId}/approve`);
        toast.success("Approved — version promoted to Approved");
      } else {
        if (!rejectReason.trim()) { toast.error("Enter a reason for rejecting."); return; }
        await api.post(`/v1/partner-portal/admin/rate-cards/versions/${pendingVersionId}/reject`, { reason: rejectReason });
        toast.success("Version rejected");
        setRejectReason("");
      }
      loadList();
      api.get(`/v1/partner-portal/admin/rate-cards/${selected}`).then((r) => setDetail(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.code === "role_denied" ? "Only founder/finance/partner can sign." : "Couldn't complete that action.");
    } finally {
      setBusyVersion(null);
    }
  };

  const initializeCard = async () => {
    setInitializing(true);
    try {
      const r = await api.post(`/v1/partner-portal/admin/rate-cards/${selected}/initialize`, { currency: seedCurrency });
      toast.success(`Blank card created (${r.data.items} lines, currency ${seedCurrency}) — the partner can now price it or mark lines Not offered`);
      loadList();
      api.get(`/v1/partner-portal/admin/rate-cards/${selected}`).then((r2) => setDetail(r2.data));
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      toast.error(code === "card_already_exists" ? "This partner already has a card." : "Couldn't initialize the card — please try again.");
    } finally {
      setInitializing(false);
    }
  };

  const seedFromGlobal = async () => {
    setSeeding(true);
    try {
      const r = await api.post(`/v1/partner-portal/admin/rate-cards/${selected}/seed-from-global`, { currency: seedCurrency });
      toast.success(`Seeded ${r.data.items} items from NJM's rate card — the card is now Approved`);
      loadList();
      api.get(`/v1/partner-portal/admin/rate-cards/${selected}`).then((r2) => setDetail(r2.data));
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      toast.error(code === "role_denied" ? "Only the founder can seed a partner's first card." : code === "card_already_exists" ? "This partner already has a card." : "Couldn't seed the card — please try again.");
    } finally {
      setSeeding(false);
    }
  };

  if (!canManage) {
    return <div className="p-8 text-sm text-[#A8A29E]" data-testid="admin-rate-cards-restricted">Access Restricted — founder/partner/pm/finance only.</div>;
  }

  return (
    <div className="p-5 sm:p-10 space-y-6" data-testid="admin-partner-rate-cards-page">
      <div className="font-editorial text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#1C1C1E] inline-flex items-center gap-2.5">
        <Wallet className="h-7 w-7 text-[#FFC817]" /> Partner Rate Cards
      </div>

      {loading ? (
        <div className="text-sm text-[#A8A29E]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-[#E7E5E4] bg-white p-8 text-center text-sm text-[#A8A29E]" data-testid="admin-rate-cards-empty">No partner rate cards issued yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div className="rounded-lg border border-[#E7E5E4] bg-white overflow-hidden" data-testid="admin-rate-cards-partner-list">
            {rows.map((row) => <PartnerListRow key={row.partner_remote_id} row={row} selected={row.partner_remote_id === selected} onSelect={setSelected} />)}
          </div>

          <div className="rounded-lg border border-[#E7E5E4] bg-white" data-testid="admin-rate-cards-detail">
            {!detail ? (
              <div className="p-8 text-center text-sm text-[#A8A29E]">Select a partner to review their card.</div>
            ) : !currentRow?.has_card ? (
              <div className="p-8 text-center" data-testid="admin-rate-cards-no-card">
                <FileText className="h-6 w-6 mx-auto mb-3 text-[#A8A29E]" />
                <p className="text-sm font-medium text-[#1C1C1E] mb-1">{detail.header.partner_name} doesn't have a Rate Card yet</p>
                <p className="text-xs text-[#A8A29E] mb-4">Create a blank, structure-only card (NJM's catalogue, no rates, no internal cost) — the partner (or a PM on their behalf) fills it in line-by-line.</p>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <select value={seedCurrency} onChange={(e) => setSeedCurrency(e.target.value)} data-testid="admin-rate-cards-seed-currency"
                    className="rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs">
                    {SEED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={initializeCard} disabled={initializing} data-testid="admin-rate-cards-initialize-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-3.5 py-1.5 text-xs font-medium text-white hover:bg-[#333] disabled:opacity-50">
                    <FileText className="h-3.5 w-3.5" /> {initializing ? "Creating…" : "Initialize Blank Card"}
                  </button>
                </div>
                {user?.role === "founder" && (
                  <div className="border-t border-dashed border-[#E7E5E4] pt-3">
                    <button onClick={() => setShowBootstrap((v) => !v)} data-testid="admin-rate-cards-toggle-bootstrap"
                      className="inline-flex items-center gap-1.5 text-[11px] text-[#A8A29E] hover:text-[#78716C]">
                      <History className="h-3 w-3" /> {showBootstrap ? "Hide" : "Show"} historical bootstrap option
                    </button>
                    {showBootstrap && (
                      <div className="mt-3">
                        <p className="text-xs text-[#A8A29E] mb-3 max-w-[320px] mx-auto">Bootstrap from NJM Benchmark (historical only) — copies NJM's own internal cost as this partner's starting price, immediately Approved. Only ever used for TVC's original migration; not the default for new partners.</p>
                        <button onClick={seedFromGlobal} disabled={seeding} data-testid="admin-rate-cards-seed-btn"
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3.5 py-1.5 text-xs font-medium text-[#78716C] hover:bg-[#FAFAF9] disabled:opacity-50">
                          <History className="h-3.5 w-3.5" /> {seeding ? "Seeding…" : "Bootstrap from NJM Benchmark (historical only)"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="border-b border-[#E7E5E4] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm font-medium text-[#1C1C1E]">{detail.header.partner_name} · {detail.header.currency} · v{detail.header.version_no}</div>
                  {currentRow?.pending_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#A8A29E]"><ShieldCheck className="h-3.5 w-3.5" /> Awaiting a single approval</span>
                  )}
                </div>

                {detail.header.completion && (
                  <div className="border-b border-[#E7E5E4] px-4 py-3" data-testid="admin-rate-cards-completion-bar">
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="font-medium text-[#78716C]">Onboarding completion (priced or not-offered)</span>
                      <span className="font-bold text-[#1C1C1E]" data-testid="admin-rate-cards-completion-pct">{detail.header.completion.pct}% ({detail.header.completion.done}/{detail.header.completion.total} decided)</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#F5F5F4] overflow-hidden">
                      <div className="h-full rounded-full bg-[#FFC817]" style={{ width: `${detail.header.completion.pct}%` }} />
                    </div>
                  </div>
                )}

                {currentRow?.pending_count > 0 && canSign && (
                  <div className="border-b border-[#E7E5E4] bg-amber-500/[0.04] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="admin-rate-cards-signature-bar">
                    <button onClick={() => act("approve")} disabled={busyVersion === pendingVersionId} data-testid="admin-rate-cards-approve-btn"
                      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </button>
                    <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason to reject…" data-testid="admin-rate-cards-reject-reason"
                      className="rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs flex-1 min-w-[160px]" />
                    <button onClick={() => act("reject")} disabled={busyVersion === pendingVersionId} data-testid="admin-rate-cards-reject-btn"
                      className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </button>
                  </div>
                )}

                <div className="divide-y divide-[#E7E5E4] max-h-[560px] overflow-y-auto">
                  {detail.items.map((item) => (
                    <div key={item.canonical_id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`admin-rate-cards-item-${item.canonical_id}`}>
                      <div className="min-w-0 flex items-center gap-2">
                        <GroupTag group={item.group} />
                        <span className="text-sm text-[#1C1C1E] truncate">{item.service_name}</span>
                        {item.is_new && <span className="rounded-full bg-[#FFC817]/20 text-[#8A6A0A] px-1.5 py-0.5 text-[9px] font-bold">NEW</span>}
                      </div>
                      <div className="text-right text-sm shrink-0">
                        {item.not_offered ? (
                          <span className="rounded-full bg-[#F5F5F4] text-[#A8A29E] px-2 py-0.5 text-[10px] font-medium" data-testid={`admin-rate-cards-item-not-offered-${item.canonical_id}`}>Not offered</span>
                        ) : item.proposed_rate !== null ? (
                          <span>
                            {item.rate !== null && <span className="line-through text-[#A8A29E] mr-1.5">{formatMoney(item.rate, detail.header.currency)}</span>}
                            <span className="font-semibold text-amber-700">{formatMoney(item.proposed_rate, detail.header.currency)}</span>
                          </span>
                        ) : item.rate !== null ? (
                          <span className="text-[#1C1C1E]">{formatMoney(item.rate, detail.header.currency)}</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wide text-[#D6D3D1]" data-testid={`admin-rate-cards-item-undecided-${item.canonical_id}`}>Undecided</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
